import React from "react";
import { graphql } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSlidersH, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact } from "lodash";

import AppContext from "~/common/AppContext";
import UserSelector, { type SingleValueType, type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { uiAvatar } from "~/lib/utils";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Popup from "~/ui/legacy/Popup";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import type { FeedbackTypeFilter } from "~/__generated__/FeedbackListQuery.graphql";
import type { FiltersUsersQuery } from "~/__generated__/FiltersUsersQuery.graphql";

import stylesArrowDropdown from "~/ui/ArrowDropdown/ArrowDropdown.module.css";

import styles from "./Filters.module.css";

// Converts user data to UserSelectorOption
function userToSelectorOption(user: {
	id: string;
	fullName: string;
	email: string;
	isGuest: boolean;
	image: string;
}): UserSelectorOption {
	return {
		value: user.id,
		label: user.fullName,
		email: user.email,
		avatar: {
			id: user.id,
			fullName: user.fullName,
			isGuest: user.isGuest,
			image: user.image,
			email: user.email,
		},
	};
}

const filtersQuery = graphql`
	query FiltersUsersQuery($userId: ID!) {
		viewer {
			user {
				internalAttributes {
					directReports {
						edges {
							node {
								id
								fullName
								email
								image
								isGuest
							}
						}
					}
				}
			}
			sentFeedback: users(first: 25, haveFeedbackAbout: true, doesNotReportTo: $userId) {
				edges {
					node {
						id
						fullName
						email
						image
						isGuest
					}
				}
			}
			sentFeedbackRequest: users(first: 25, sentFeedbackRequest: true, doesNotReportTo: $userId) {
				edges {
					node {
						id
						fullName
						email
						image
						isGuest
					}
				}
			}
		}
	}
`;

export const TYPE_VALUES = ["THREE_SIXTY", "MEETING", "OTHER"] as const; // same as FeedbackTypeFilter

export type TypeValueType = FeedbackTypeFilter;

export const TYPE_TEXT_MAP = {
	THREE_SIXTY: "360 feedback",
	MEETING: "Meeting feedback",
	OTHER: "Other feedback",
} as const;

interface DropdownItemsProps {
	value: TypeValueType;
	onClick: (v: TypeValueType) => void;
	className?: string;
}

function ValueDropdownItem({
	value,
	onClick: _onClick,
	children,
	className,
}: React.PropsWithChildren<DropdownItemsProps>) {
	const onClick = React.useCallback(() => _onClick(value), [value, _onClick]);
	return (
		<DropdownItem onClick={onClick} className={className}>
			{children}
		</DropdownItem>
	);
}

function TypeField({ value, onChange }: { value?: TypeValueType; onChange: (v?: TypeValueType) => void }) {
	const onClear = React.useCallback(() => onChange(undefined), [onChange]);

	return (
		<>
			<p className={styles.label}>
				Type
				{value && (
					<ButtonLink onClick={onClear} className={styles.link}>
						Clear
					</ButtonLink>
				)}
			</p>
			<ArrowDropdown
				compact
				heading="Type"
				text={value ? TYPE_TEXT_MAP[value] : "All feedback"}
				className={styles.dropdown}
				textClassName={cx(styles.dropdownText, { [styles.placeholderText]: !value })}
			>
				{TYPE_VALUES.map(elem => (
					<ValueDropdownItem
						key={elem}
						value={elem}
						onClick={onChange}
						className={cx(styles.dropdownItem, { [styles.dropdownItemActive]: value === elem })}
					>
						{TYPE_TEXT_MAP[elem]}
						{value === elem && <FontAwesomeIcon icon={faCheck} fixedWidth className={styles.checkIcon} />}
					</ValueDropdownItem>
				))}
			</ArrowDropdown>
		</>
	);
}

function UserField({
	label,
	value,
	includeSelections,
	onChange: _onChange,
}: {
	label: string;
	value?: string;
	includeSelections?: Array<UserSelectorOption>;
	onChange: (v?: string) => void;
}) {
	const onClear = React.useCallback(() => _onChange(undefined), [_onChange]);

	const selectedUser = value ? includeSelections?.find(selection => selection.value === value) || null : null;

	const onChange = React.useCallback(
		(option: SingleValueType<UserSelectorOption>) => {
			if (option) {
				_onChange(option.value);
			} else {
				// User cleared the selection - this means "all users"
				onClear();
			}
		},
		[_onChange, onClear],
	);

	return (
		<>
			<p className={styles.label}>
				{label}
				{value && (
					<ButtonLink onClick={onClear} className={styles.link}>
						Clear
					</ButtonLink>
				)}
			</p>
			<UserSelector
				selectedUser={selectedUser}
				onChange={onChange}
				className={styles.userSelect}
				includedOptions={includeSelections}
				placeholderIcon={faUser}
				isClearable={false}
				placeholder="Select user..."
			/>
		</>
	);
}

interface Props {
	type?: TypeValueType;
	about?: string; // user.id
	from?: string; // user.id
	setType: (v?: TypeValueType) => void;
	setAbout: (v?: string) => void;
	setFrom: (v?: string) => void;
}

export default function Filters({ type, about, from, setType, setAbout, setFrom }: Props) {
	const [active, setActive] = React.useState(false);
	const onOpen = React.useCallback(() => setActive(true), [setActive]);
	const onClose = React.useCallback(() => setActive(false), [setActive]);
	const numValues = compact([type, about, from]).length;

	const triggerElement = (
		<Button size="wide" type="button" variant="secondary" className={cx(styles.button, { [styles.active]: active })}>
			{numValues === 0 ? (
				<FontAwesomeIcon icon={faSlidersH} className={styles.icon} />
			) : (
				<span className={styles.badge}>{numValues}</span>
			)}
			<p className={styles.text}>Filters</p>
		</Button>
	);

	return (
		<Popup
			basic
			trigger={triggerElement}
			className={stylesArrowDropdown.popup}
			on="click"
			position="bottom right"
			open={active}
			onOpen={onOpen}
			onClose={onClose}
		>
			<React.Suspense fallback={null}>
				<FiltersPopupContent
					about={about}
					from={from}
					type={type}
					setAbout={setAbout}
					setFrom={setFrom}
					setType={setType}
				/>
			</React.Suspense>
		</Popup>
	);
}

interface PopupContentProps {
	type?: TypeValueType;
	about?: string; // user.id
	from?: string; // user.id
	setType: (v?: TypeValueType) => void;
	setAbout: (v?: string) => void;
	setFrom: (v?: string) => void;
}

function FiltersPopupContent({ about, from, type, setAbout, setFrom, setType }: PopupContentProps) {
	const { user } = React.useContext(AppContext);
	const { viewer } = useLazyLoadQuery<FiltersUsersQuery>(
		filtersQuery,
		{
			userId: user?.id || "",
		},
		{},
	);

	const aboutMeOption: UserSelectorOption = React.useMemo(
		() => ({
			value: user?.id || "0",
			label: "About me",
			avatar: {
				id: user?.id || "0",
				fullName: "About me",
				isGuest: false,
				image: uiAvatar(faUser, { background: "000000" }),
			},
		}),
		[user],
	);

	const aboutIncludeSelections = React.useMemo(() => {
		let includeSelections = [aboutMeOption];
		if (viewer.user.internalAttributes?.directReports) {
			includeSelections = includeSelections.concat(
				viewer.user.internalAttributes.directReports.edges.map(edge => userToSelectorOption(edge.node)),
			);
		}
		if (viewer.sentFeedback) {
			includeSelections = includeSelections.concat(
				viewer.sentFeedback.edges.map(edge => userToSelectorOption(edge.node)),
			);
		}
		return includeSelections;
	}, [viewer, aboutMeOption]);

	const fromIncludeSelections = React.useMemo(() => {
		return viewer.sentFeedbackRequest
			? viewer.sentFeedbackRequest.edges.map(edge => userToSelectorOption(edge.node))
			: [];
	}, [viewer]);

	return (
		<div className={styles.content}>
			<TypeField value={type} onChange={setType} />
			<UserField label="About" value={about} onChange={setAbout} includeSelections={aboutIncludeSelections} />
			<UserField label="From" value={from} onChange={setFrom} includeSelections={fromIncludeSelections} />
		</div>
	);
}
